import { Injectable } from "@angular/core";
import * as moment from "moment";
import {
  Content,
  ContentApi,
  Emoji,
  EmojiApi,
  Game,
  GameApi,
  GameCategoryApi,
  LoopBackConfig,
  Player,
  PlayerApi,
  Prize,
  Resource,
  ResourceApi,
} from "@lbsdk/*";
import { BehaviorSubject, forkJoin, Observable, Subject } from "rxjs";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import { FormArray, FormBuilder, FormGroup } from "@angular/forms";
import { environment } from "@env/environment";
import { FuseSplashScreenService } from "@fuse/services/splash-screen.service";
import * as shuffle from "shuffle-array";
import { MatSnackBar } from "@angular/material/snack-bar";
import * as _ from "lodash";
import { PrizeService } from "@app/main/core/services/prize.service";
import { prizeButtons } from "./prize-buttons";

export interface ColorWheel {
  type: "img" | "java" | "css" | "tie_breaker";
  data: {
    img?: {
      file: any;
      src: any;
    };
    cssCenterChoice?: "emoji" | "greyCoin" | "blankCoin";
    color_1: string;
    color_2: string;
    color_3: string;
    color_4: string;
    color_5: string;
    color_6: string;
  };
}

export const fillArray = <T extends object>(
  length: number,
  defaultValue: T
): T[] =>
  length >= 1 && length <= 48
    ? Array.from({ length }, () => _.cloneDeep(defaultValue))
    : [];

@Injectable({
  providedIn: "root",
})
export class GameSettingsService {
  winPattern: any[] = [];
  autoSelectPrizes: any[] = [];
  audioTypes = [
    "audio/m4p",
    "audio/mp3",
    "audio/mp3",
    "audio/flac",
    "audio/mp4",
    "audio/wav",
    "audio/wma",
    "audio/aac",
    "audio/3gp",
    "audio/aa",
    "audio/aax",
    "audio/act",
    "audio/aiff",
    "audio/alac",
    "audio/amr",
    "audio/ape",
    "audio/au",
    "audio/awb",
    "audio/dct",
    "audio/dss",
    "audio/dvf",
    "audio/gsm",
    "audio/iklax",
    "audio/ivs",
    "audio/m4a",
    "audio/m4b",
    "audio/mmf",
    "audio/mpc",
    "audio/msv",
    "audio/wv",
    "audio/webm",
  ];

  imagesType = ["image/jpeg", "image/png", "image/jpg", "image/gif"];

  defaultEmojis: Emoji[] = [];
  emojis: Emoji[] = [];
  gameEmojis: any[] = [];
  onDefaultEmojisChanged: BehaviorSubject<any> = new BehaviorSubject({});
  onResourcesChanged: BehaviorSubject<any> = new BehaviorSubject<any>({});
  onEmojisChanged: BehaviorSubject<any> = new BehaviorSubject({});
  onGameEmojisChanged: BehaviorSubject<any> = new BehaviorSubject({});
  contents: any[] = [];
  onGameContentsChanged: BehaviorSubject<any> = new BehaviorSubject({});

  prizes: Prize[] = [];
  players: Player[] = [];
  game: Game = new Game();
  prizeMode: any;

  categories: any[] = [];
  onCategoriesChanged: BehaviorSubject<any> = new BehaviorSubject({});
  routeParams: any;
  queryParams: any;
  extra: Emoji[] = [];
  screensEmojis: Emoji[] = [];
  inActiveprize: Prize[] = [];
  populated = false;
  prizeForm: FormGroup;
  prizeControls: any[] = [];
  noOfPlayers: any;
  gameLogoUploadPath: any;
  powerBarUploadPath: any;
  thumbnailUploadPath: any;
  powerBarAudioUploadPath: any;
  prizeImageUploadPath: any;
  emojiUploadPath: any;
  gameMenuArtUploadPath: any;
  gameMenuLayerUploadPath: any;
  automatchButtonArtUploadPath: any;
  selfPickButtonArtUploadPath: any;
  // FINAL X p28-36 Dress Up. Reuses the game-menu-art bucket path, same as
  // automatchButtonArt/selfPickButtonArt above — no new upload path.
  dressUpFigureUploadPath: any;
  inviteImageArtUploadPath: any;
  imageRulesUploadPath: any;
  sfxSoundUploadPath: any;
  rewardUploadPath: any;

  selectedPlayers: any[] = [];

  gameEditMode: any = {};

  restartThreshold: Resource;

  popupData: any;

  onPopupDataChanged: Subject<any> = new Subject<boolean>();

  extraEmojisLength: any;

  latitude: number;
  longitude: number;
  radius: number = 1;
  unit: string = "feet";

  grids: { noOfCol: string; key: string; gameFormat: string; img: string; title: string }[] = [
    { noOfCol: "4", key: "4", gameFormat: "standard", img: "/assets/images/4GridButton.png", title: "4x4 Grid" },
    { noOfCol: "2", key: "2", gameFormat: "standard", img: "/assets/images/2GridButton.png", title: "2x3 Grid" },
    { noOfCol: "6", key: "6", gameFormat: "standard", img: "/assets/images/48GridButton.png", title: "48 Grid" },
    { noOfCol: "6", key: "seatMap", gameFormat: "seatMap", img: "/assets/images/seat-map.jpeg", title: "Seat Map" },
    { noOfCol: "6", key: "dressUp", gameFormat: "dressUp", img: "/assets/images/dress-up.jpeg", title: "Dress Up" },
    { noOfCol: "6", key: "autoTriggerMatch", gameFormat: "autoTriggerMatch", img: "/assets/images/automatch-stadium.jpeg", title: "AutoMatch" },
  ];

  shareableContents: any[] = [];

  extraEmojisSet: any[] = [];

  isDuplicationFlag = false;

  colorWheel: ColorWheel = this.getDefaultColorWheel();

  tieBreakerColorWheel: any = this.getDefaultTieBreakerColorWheel();

  /**
   * Types: color, emoji, canvas
   */
  prizeConfetti = this.getDefaultPrizeConfetti();

  getDefaultColorWheel(): ColorWheel {
    return {
      type: "img",
      data: {
        color_1: "#FFA500",
        color_2: "#FFEF00",
        color_3: "#00FF00",
        color_4: "#0000FF",
        color_5: "#800080",
        color_6: "#FF0000",
      },
    };
  }

  getDefaultTieBreakerColorWheel(): any {
    return {
      type: "tie_breaker",
      data: [
        { color: "#FFA500", image: "https://i.pravatar.cc/150?img=11" },
        { color: "#FFEF00", image: "https://i.pravatar.cc/150?img=5" },
        { color: "#00FF00", image: "https://i.pravatar.cc/150?img=3" },
        { color: "#0000FF", image: "https://i.pravatar.cc/150?img=12" },
        { color: "#800080", image: "https://i.pravatar.cc/150?img=9" },
        { color: "#FF0000", image: "https://i.pravatar.cc/150?img=8" },
      ],
    };
  }

  getDefaultPrizeConfetti(): any[] {
    return fillArray(48, {
      type: "color",
      color: {
        color_1: "#7969ec",
        color_2: "#60e0e6",
        color_3: "#69ddec",
        color_4: "#b0e0e6",
        color_5: "#ff85de",
        color_6: "#00e1ff",
        numberInput: 10,
        radius: 10,
        type: "color",
      },
      emoji: {
        emoji_1: "🌈",
        emoji_2: "⚡",
        emoji_3: "💥",
        emoji_4: "✨",
        emoji_5: "💫",
        emoji_6: "🌸",
        numberInput: 10,
        size: 10,
        type: "emoji",
      },
      canvas: {
        particleCount: 200,
        spread: 100,
        type: "canvas",
      },
    });
  }

  resetState() {
    this.winPattern = [];
    this.autoSelectPrizes = [];
    this.gameEmojis = [];
    this.game = new Game();
    this.prizeMode = undefined;
    this.extra = [];
    this.screensEmojis = [];
    this.populated = false;
    this.prizeControls = [];
    this.selectedPlayers = [];
    this.gameEditMode = {};
    this.popupData = undefined;
    this.shareableContents = [];
    this.isDuplicationFlag = false;

    this.colorWheel = this.getDefaultColorWheel();
    this.tieBreakerColorWheel = this.getDefaultTieBreakerColorWheel();
    this.prizeConfetti = this.getDefaultPrizeConfetti();

    this.isGameMenuToolbar = false;
    this.similarGames = [];
    this.selectedSimilarGames = [];
    this.currentTabs = [];
  }

  rewardAmount: any;

  cashPrizeImage: any;

  private emojiAddedSubject = new BehaviorSubject<any>(null);
  emojiAdded$ = this.emojiAddedSubject.asObservable();

  private removedEmojisSubject = new BehaviorSubject<any[]>([]);
  removedEmojis$ = this.removedEmojisSubject.asObservable();

  private emojisPopulatedSubject = new Subject<void>();
  emojisPopulated$ = this.emojisPopulatedSubject.asObservable();

  currentTabs: any[] = [];

  defaultColorRing: Resource | null = null;
  defaultCoinIcon: Resource | null = null;
  greyCoinIcon: Resource | null = null;
  blankCoinIcon: Resource | null = null;

  isGameMenuToolbar: boolean = false;

  similarGames: any[] = [];

  selectedSimilarGames: any[] = [];

  constructor(
    private http: HttpClient,
    private emojiApi: EmojiApi,
    private prizeService: PrizeService,
    private gameApi: GameApi,
    private resourceApi: ResourceApi,
    private formBuilder: FormBuilder,
    private playerApi: PlayerApi,
    private categoryApi: GameCategoryApi,
    private snackBar: MatSnackBar,
    private contentApi: ContentApi,
    private _fuseSplashScreenService: FuseSplashScreenService
  ) {
    this.gameLogoUploadPath =
      environment.mainContainer + "/" + environment.gameLogos;
    this.prizeImageUploadPath =
      environment.mainContainer + "/" + environment.prizes;
    this.emojiUploadPath = environment.mainContainer + "/" + environment.emojis;
    this.gameMenuArtUploadPath =
      environment.mainContainer + "/" + environment.gameMenuArt;
    this.gameMenuLayerUploadPath =
      environment.mainContainer + "/" + environment.gameMenuLayer;
    this.automatchButtonArtUploadPath =
      environment.mainContainer + "/" + environment.gameMenuArt;
    this.selfPickButtonArtUploadPath =
      environment.mainContainer + "/" + environment.gameMenuArt;
    this.dressUpFigureUploadPath =
      environment.mainContainer + "/" + environment.gameMenuArt;
    this.inviteImageArtUploadPath =
      environment.mainContainer + "/" + environment.inviteImageArt;
    this.sfxSoundUploadPath =
      environment.mainContainer + "/" + environment.sfxSound;

    this.rewardUploadPath =
      environment.mainContainer + "/" + environment.rewardUploadPath;

    this.powerBarUploadPath =
      environment.mainContainer + "/" + environment.powerBarUpload;
    this.powerBarAudioUploadPath =
      environment.mainContainer + "/" + environment.powerBarAudioUploadPath;

    this.imageRulesUploadPath =
      environment.mainContainer + "/" + environment.imageRulesUploadPath;

    this.thumbnailUploadPath =
      environment.mainContainer + "/" + environment.thumbnailUploadPath;
  }

  /**
   * Resolve
   * @param {ActivatedRouteSnapshot} route
   * @param {RouterStateSnapshot} state
   * @returns {Observable<any> | Promise<any> | any}
   */
  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<any> | Promise<any> | any {
    let newGameFlag = false;
    try {
      if (!_.isUndefined(localStorage["newGameFlag"]) && localStorage["newGameFlag"] !== "") {
        newGameFlag = JSON.parse(localStorage["newGameFlag"]);
      }
    } catch (e) {
      newGameFlag = false;
    }
    if (newGameFlag) this.deleteCookies();
    if (route.params) {
      this.routeParams = route.params;
      if (!this.routeParams.id) {
        this.resetState();
      }
    }
    return new Promise<void>((resolve, reject) => {
      Promise.all([
        this.getDefaultEmojis(),
        this.getEmojis(),
        this.getPrizes(),
        this.getCategories(),
        this.getGameEmojis(),
        this.getInActivePrizes(),
        this.getGameWithEmojisAndPrizes(),
        this.getNoOfPlayers(),
        this.getResource(),
        this.getCashPrizeImage(),
        this.getDefaultColorRing(),
        this.getDefaultCoinIcons(),
        this.getContent(),
        this.getGameEditingMode(),
        this.getPositiveRewardAmount(),
      ]).then(() => {
        resolve();
      }, reject);
    });
  }

  getDefaultCoinIcons(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.resourceApi
        .find({
          where: {
            key: { inq: ["points", "greyCoin", "blankCoin"] },
          },
        })
        .subscribe((res: any[]) => {
          const result = {
            points: res.find((item) => item.key === "points") || null,
            greyCoin: res.find((item) => item.key === "greyCoin") || null,
            blankCoin: res.find((item) => item.key === "blankCoin") || null,
          };

          this.defaultCoinIcon = result.points || null;
          this.greyCoinIcon = result.greyCoin || null;
          this.blankCoinIcon = result.blankCoin || null;

          resolve(result);
        }, reject);
    });
  }

  getDefaultColorRing(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.resourceApi
        .find({ where: { key: "colorRing" } })
        .subscribe((res: any) => {
          this.defaultColorRing = res[0];
          resolve(this.defaultColorRing);
        }, reject);
    });
  }

  getPositiveRewardAmount(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.resourceApi
        .find({ where: { key: "positiveRewardPoints" } })
        .subscribe(
          (resp: any) => {
            this.rewardAmount = resp[0].value;
            resolve(this.rewardAmount);
          },
          (error) => reject(error)
        );
    });
  }

  getCashPrizeImage(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.resourceApi.find({ where: { key: "cashPrize" } }).subscribe(
        (resp: any) => {
          this.cashPrizeImage = resp[0];
          resolve(this.cashPrizeImage);
        },
        (error) => reject(error)
      );
    });
  }

  updateEmojiAdded(emoji: any) {
    this.emojiAddedSubject.next(emoji);
  }

  updateRemovedEmojisArray(array: any[]) {
    this.removedEmojisSubject.next(array);
  }

  triggerEmojisPopulated() {
    this.emojisPopulatedSubject.next();
  }

  getResource(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.resourceApi
        .find({ where: { key: "restartThreshold" } })
        .subscribe((response: any) => {
          this.restartThreshold = response;
          this.onDefaultEmojisChanged.next(this.restartThreshold);
          resolve(response);
        }, reject);
    });
  }

  getDefaultEmojis(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.emojiApi
        .find({
          where: { and: [{ isActive: true }, { emojiType: "default" }] },
        })
        .subscribe((response: any) => {
          this.defaultEmojis = response;

          // Only a NEW game starts from the defaults (or its own unsaved
          // draft). On edit/restart getGameEmojis owns extraEmojisSet: both
          // requests run in parallel from resolve(), and if this one landed
          // last it replaced the game's GameEmoji rows with plain Emoji docs
          // that have no emojiId -- a restart then saved 48 rows without one
          // and the app's gameplay screen never finished loading.
          if (!this.routeParams || !this.routeParams.id) {
            let emojis = JSON.parse(localStorage.getItem("emojis"));
            if (emojis && emojis.length !== 0) {
              this.extraEmojisSet = [...emojis];
            } else {
              this.extraEmojisSet = [...this.defaultEmojis];
            }
          }
          this.onDefaultEmojisChanged.next(this.defaultEmojis);
          resolve(response);
        }, reject);
    });
  }

  getPlayers(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.playerApi.find().subscribe(
        (players: Player[]) => {
          this.players = players;
          resolve(players);
        },
        (error) => {
          alert("Server Down");
          reject();
        }
      );
    });
  }

  getContent(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.contentApi
        .find({ order: "createdAt ASC", include: ["player", "gameCategory"] })
        .subscribe((contents: Content[]) => {
          this.contents = contents;
          this.onGameContentsChanged.next(this.contents);
          resolve(this.contents);
        }, reject);
    });
  }

  getShareableContentWithGameCategoryId(categoryId) {
    return this.contentApi.find({
      where: { type: "shareable content", categoryId: categoryId },
      order: "createdAt ASC",
      include: ["player", "gameCategory"],
    });
  }

  getGameEditingMode(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.routeParams.id) {
        this.gameApi
          .getGameWithEmojisAndPrizes(this.routeParams.id)
          .subscribe((game: Game) => {
            this.gameEditMode = { ...this.getGameEditingMode, ...game };
            // this.gameEditMode.prizes.forEach((prize) => {});
            resolve(this.gameEditMode);
          }, reject);
      } else {
        resolve("game not found");
      }
    });
  }

  getGameWithEmojisAndPrizes(): Promise<any> {
    this.prizeControls = [];
    if (this.routeParams.id) {
      return new Promise((resolve, reject) => {
        this.gameApi
          .getGameWithEmojisAndPrizes(this.routeParams.id)
          .subscribe((response: any) => {
            this.game = response;
            if (this.game.prizes) {
              this.game.prizes.forEach((prize) => {
                if (prize.type === "exact match" || prize.type === "solo") {
                  this.prizeControls[prize.position] = {
                    ["type" + prize.position]: prize.type,
                    ["cashPrizeValue" + prize.position]: prize.cashPrizeValue,
                  };
                } else if (
                  prize.type === "auto select" ||
                  prize.type === "top 6"
                ) {
                  this.prizeControls[prize.position] = {
                    ["type" + prize.position]: prize.type,
                    ["cashPrizeValue" + prize.position]: "",
                  };
                } else {
                  this.prizeControls[prize.position] = {
                    ["type" + prize.position]: "",
                    ["cashPrizeValue" + prize.position]: "",
                  };
                }
              });
            }
            if (
              prizeButtons.map((el) => el.type).includes(this.game.prizeMode)
            ) {
              const numOfPrizes = prizeButtons.find(
                (el) => el.type === this.game.prizeMode
              ).numOfPrizes;
              this.createPrizeForm(numOfPrizes);
              this.prizeForm.patchValue({ prize: this.prizeControls });
            } else {
              const numOfPrizes = prizeButtons.find(
                (el) => el.type === "auto select mode"
              ).numOfPrizes;
              this.createPrizeForm(numOfPrizes);
              this.prizeForm.patchValue({ prize: this.prizeControls });
            }
            resolve(this.prizeForm);
          }, reject);
      });
    }
  }

  createPrizeForm(noOfControls) {
    this.prizeForm = this.formBuilder.group({
      prize: this.formBuilder.array([]),
    });
    for (let i = 0; i < noOfControls; i++) {
      const control = <FormArray>this.prizeForm.controls["prize"];
      control.push(this.creatingPrizeControls(i));
    }
  }

  creatingPrizeControls(i) {
    const types = "type" + i;
    const cashPrizeValues = "cashPrizeValue" + i;
    return this.formBuilder.group({
      [types]: [""],
      [cashPrizeValues]: [""],
    });
  }

  getExtraEmojis(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.emojiApi
        .find({
          where: { emojiType: "regular", isActive: true },
          order: ["createdAt DESC", "position ASC"],
          limit: 48,
        })
        .subscribe((emojis: Emoji[]) => {
          resolve(emojis);
        }, reject);
    });
  }

  getEmojis(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.emojiApi
        .find({
          where: { isActive: true },
          order: ["createdAt DESC", "position ASC"],
        })
        .subscribe((response: any) => {
          this.emojis = response;
          this.extraEmojisLength = this.emojis.filter(
            (emoji) => emoji.emojiType === "regular"
          ).length;
          this.onEmojisChanged.next(this.emojis);
          resolve(response);
        }, reject);
    });
  }

  getGameEmojis(id?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.routeParams.id) {
        id = this.routeParams.id;
        this.gameApi
          .getEmojis(id, { include: "emoji", order: "position ASC" })
          .subscribe((gameEmojis: any[]) => {
            if (gameEmojis.length !== 0) {
              this.gameEmojis = [...gameEmojis];
              // Never the localStorage "emojis" draft here: it is only ever
              // written while creating a new game, so on edit/restart it can
              // only be a leftover from an abandoned create.
              this.extraEmojisSet = [...this.gameEmojis];
              this.onGameEmojisChanged.next(this.gameEmojis);
            }
            resolve(gameEmojis);
          }, reject);
      } else {
        resolve([]);
      }
    });
  }

  shufflePrizes(emojis?) {
    let emojisSet = emojis ? emojis : this.gameEmojis;
    let shuffledEmojis = shuffle.pick(emojisSet, { picks: 6 });
    shuffledEmojis.forEach((el, index) => (el.index = index));
    this.winPattern.forEach((pattern) => {
      pattern.cashPrizeImages =
        emojisSet.length !== 48
          ? alert("Please keep 48 emojis in screen")
          : shuffledEmojis;
    });
  }

  createEmojis(game, gameId, emojis, status): Promise<any> {
    return new Promise((resolve, reject) => {
      this.gameApi.batchEmojis(gameId, emojis, status).subscribe(
        (response) => {
          if (game.gameType === "new" || game.gameType === "restarted") {
            this.sendContentNotification(response, gameId).then(
              (res) => {
                resolve(response);
              },
              (error) => {
                alert(error);
                reject();
              }
            );
          } else {
            resolve(response);
          }
        },
        (error) => {
          alert(error);
          reject();
        }
      );
    });
  }

  sendContentNotification(emojis, gameId): Promise<any> {
    return new Promise((resolve, reject) => {
      this.gameApi.sendContentNotification(gameId, emojis).subscribe((res) => {
        resolve(res);
      }, reject);
    });
  }

  getExtraEmojisCount(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.emojiApi
        .count({
          or: [{ emojiType: "regular" }, { emojiType: "default" }],
          isActive: true,
        })
        .subscribe((res) => {
          this.extraEmojisLength = res.count;
          resolve(this.extraEmojisLength);
        }, reject);
    });
  }

  getPrizes(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.prizeService
        .getPrizes({ where: { isActive: true } })
        .subscribe((response: any) => {
          this.prizes = response;
          resolve(response);
        }, reject);
    });
  }

  getInActivePrizes(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.prizeService
        .getPrizes({ where: { isActive: false } })
        .subscribe((response: any) => {
          this.inActiveprize = response;
          resolve(response);
        }, reject);
    });
  }

  getCategories(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.categoryApi.find().subscribe((response: any) => {
        this.categories = response;
        this.onCategoriesChanged.next(this.categories);
        resolve(response);
      }, reject);
    });
  }

  deleteExtraEmojis(emojis: Emoji[]): Promise<any> {
    return new Promise((resolve, reject) => {
      this.emojiApi.batchUpdate(emojis).subscribe((response: any) => {
        this.getExtraEmojis().then((extraEmojis) => {
          this.getExtraEmojisCount().then(() => {
            resolve(extraEmojis);
          });
        });
      }, reject);
    });
  }

  deletePrizes() {
    return this.gameApi.deletePrizes(this.routeParams.id);
  }

  getSelectedPlayersData(selectedPlayers): Promise<any> {
    return new Promise((resolve, reject) => {
      this.playerApi
        .find({ where: { id: { inq: selectedPlayers } } })
        .subscribe((players) => {
          resolve(players);
        }, reject);
    });
  }

  getNoOfPlayers() {
    this.playerApi.count().subscribe((players) => {
      this.noOfPlayers = players.count;
    });
  }

  creatingPopUpFormControls(i) {
    const contentTabTypes = "contentTabType" + i;
    const adTypes = "adType" + i;
    const popUps = "popUp" + i;
    const countDowns = "countDown" + i;
    const countDownTextAreaInputs = "countDownTextAreaInput" + i;
    const countDownImages = "countDownImage" + i;
    const countDownImagesIsExpand = "countDownImageIsExpand" + i;
    const countDownEmojis = "countDownEmoji" + i;
    const adModes = "adMode" + i;
    const manualAdUrls = "manualAdUrl" + i;
    const sfxSounds = "sfxSound" + i;
    const selectedQueId = "selectedQueId" + i;
    const selectedAdQueId = "selectedAdQueId" + i;
    return this.formBuilder.group({
      [sfxSounds]: ["sfxSound"],
      [contentTabTypes]: ["CONTENT"],
      [popUps]: [{}],
      [countDowns]: [0],
      [countDownTextAreaInputs]: [null],
      [countDownImages]: [null],
      [countDownImagesIsExpand]: [false],
      [countDownEmojis]: [null],
      [adTypes]: ["ad"],
      [adModes]: ["adMob/popUp"],
      [manualAdUrls]: [""],
      [selectedAdQueId]: [""],
      [selectedQueId]: [""],
    });
  }

  uploadImage(fileOrBase64: any, uploadPath: string, adminId: string) {
    let file: Blob | File;

    // Check if input is base64
    if (typeof fileOrBase64 === "string" && fileOrBase64.startsWith("data:")) {
      file = this.base64ToBlob(fileOrBase64);
    } else if (
      fileOrBase64 &&
      fileOrBase64.target &&
      fileOrBase64.target.files
    ) {
      file = fileOrBase64.target.files[0]; // real file input
    } else {
      file = fileOrBase64; // assume already a File/Blob
    }

    const formData = new FormData();
    formData.append("file", file, "upload.png"); // give a filename

    const headers = new HttpHeaders();
    headers.append("Accept", "application/json"); // Content-Type is auto set by Angular

    const params = { access_token: adminId };

    const url = `${LoopBackConfig.getPath()}/${LoopBackConfig.getApiVersion()}/containers/${encodeURIComponent(
      uploadPath
    )}/upload`;

    return this.http.post(url, formData, { headers, params });
  }

  // Helper function to convert base64 string to Blob
  base64ToBlob(base64: string): Blob {
    const arr = base64.split(",");
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  uploadRewardImages(
    rewards: any[],
    uploadPath: string,
    adminId: string
  ): Promise<any[]> {
    var limitedRewards = rewards.slice(0, 6);

    // helper to upload if base64
    var uploadIfBase64 = function (value: any): Promise<string> {
      if (typeof value === "string" && value.indexOf("data:") === 0) {
        return this.uploadImage(value, uploadPath, adminId)
          .toPromise()
          .then(function (uploaded: any) {
            if (
              uploaded &&
              uploaded.result &&
              uploaded.result.files &&
              uploaded.result.files.file &&
              uploaded.result.files.file[0] &&
              uploaded.result.files.file[0].providerResponse &&
              uploaded.result.files.file[0].providerResponse.name
            ) {
              return uploaded.result.files.file[0].providerResponse.name;
            }
            return value;
          })
          .catch(function () {
            return value;
          });
      }
      return Promise.resolve(value);
    }.bind(this);

    // process all rewards in parallel
    var rewardPromises = limitedRewards.map(function (reward) {
      var updatedReward = Object.assign({}, reward);
      var uploadTasks: Promise<any>[] = [];

      if (reward.thumbnail && reward.thumbnail.image) {
        uploadTasks.push(
          uploadIfBase64(reward.thumbnail.image).then(function (url) {
            updatedReward.thumbnail.image = url;
          })
        );
      }

      if (reward.background && reward.background.image) {
        uploadTasks.push(
          uploadIfBase64(reward.background.image).then(function (url) {
            updatedReward.background.image = url;
          })
        );
      }

      if (reward.coinIcon) {
        uploadTasks.push(
          uploadIfBase64(reward.coinIcon).then(function (url) {
            updatedReward.coinIcon = url;
          })
        );
      }

      return Promise.all(uploadTasks).then(function () {
        return updatedReward;
      });
    });

    return Promise.all(rewardPromises);
  }

  // todo: need only one function to upload any kind of file
  upload($event, uploadPath, adminId): Promise<any> {
    if (!$event) return;
    // Handle both direct File object and Event object
    let file;
    if ($event.target && $event.target.files) {
      file = $event.target.files[0];
    } else {
      file = $event;
    }

    if (!file) return;

    const formData = new FormData();
    // Generate unique filename to preserve extension and avoid server-side renaming/extension stripping
    const timestamp = new Date().getTime();
    const nameParts = (file.name || 'upload').split('.');
    const ext = nameParts.length > 1 ? '.' + nameParts.pop() : '';
    const nameWithoutExt = nameParts.join('.');
    const uniqueName = `${nameWithoutExt}-${timestamp}${ext}`;

    formData.append("file", file, uniqueName);

    const headers = new HttpHeaders();
    headers.append("Content-type", "multipart/form-data");
    headers.append("Accept", "application/json");

    const params = {
      access_token: adminId,
    };

    const url: string =
      LoopBackConfig.getPath() +
      "/" +
      LoopBackConfig.getApiVersion() +
      "/containers/" +
      encodeURIComponent(uploadPath) +
      "/upload";
    return new Promise((resolve, reject) => {
      if (file.type.includes("image")) {
        this.http
          .post(url, formData, { headers: headers, params: params })
          .subscribe((response: any) => {
            resolve(response);
            this.snackBar.dismiss();
          }, reject);
      } else {
        this._fuseSplashScreenService.hide();
        this.snackBar.open("File type not allowed", "OK", {
          verticalPosition: "top",
        });
        reject();
      }
    });
  }

  // todo: need only one function to upload any kind of file
  uploadSound($event, uploadPath, adminId) {
    if ($event.target.files.length) {
      const file = $event.target.files[0];
      const formData = new FormData();
      formData.append("file", file);

      const headers = new HttpHeaders();
      headers.append("Content-type", "multipart/form-data");
      headers.append("Accept", "application/json");

      const params = {
        access_token: adminId,
      };

      const url: string =
        LoopBackConfig.getPath() +
        "/" +
        LoopBackConfig.getApiVersion() +
        "/containers/" +
        encodeURIComponent(uploadPath) +
        "/upload";
      return new Promise((resolve, reject) => {
        if (file.type.includes("audio")) {
          this.http
            .post(url, formData, { headers: headers, params: params })
            .subscribe((response: any) => {
              resolve(response);
              this.snackBar.dismiss();
            }, reject);
        } else {
          this.snackBar.open("File type not allowed", "OK", {
            verticalPosition: "top",
          });
          reject();
        }
      });
    }
  }

  // TODO: remove upload and uploadSound function after making changes in .ts file
  // need to create a shared service where functions like upload file will be available for
  // every component to use
  uploadFile(file, uploadPath: string, adminId: string): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);

    const headers = new HttpHeaders();
    headers.append("Content-type", "multipart/form-data");
    headers.append("Accept", "application/json");

    const params = {
      access_token: adminId,
    };

    const url: string =
      LoopBackConfig.getPath() +
      "/" +
      LoopBackConfig.getApiVersion() +
      "/containers/" +
      encodeURIComponent(uploadPath) +
      "/upload";
    return new Promise((resolve, reject) => {
      this.http
        .post(url, formData, { headers: headers, params: params })
        .subscribe(
          (response: any) => {
            this.snackBar.dismiss();
            resolve(response);
          },
          (error) => reject(error)
        );
    });
  }

  imageTypeCheck(file) {
    return (
      this.imagesType.filter((type) => _.isEqual(file.type, type)).length !== 0
    );
  }

  updateContent(data): Promise<any> {
    return new Promise((resolve, reject) => {
      this.contentApi.patchAttributes(data.id, data).subscribe((res) => {
        resolve(res);
      }, reject);
    });
  }

  deleteContent(contentId): Promise<any> {
    return new Promise((resolve, reject) => {
      this.contentApi.deleteById(contentId).subscribe((res) => {
        resolve(res);
      }, reject);
    });
  }

  deleteQue(data): Promise<any> {
    return new Promise((resolve, reject) => {
      this.contentApi
        .findById(data.id, { include: ["player", "gameCategory"] })
        .subscribe((content: any) => {
          content.data.splice(data.position, 1);
          content.data.forEach((que, index) => {
            content.data[index].position = index;
          });
          if (content.data.length === 0) {
            this.deleteContent(content.id).then((res) => {
              resolve(res);
            }, reject);
          } else {
            resolve(content);
          }
        }, reject);
    });
  }

  initializeInvites() {
    const invites = [];
    for (let i = 0; i < 6; i++) {
      invites[i] = {
        id: i,
        name: "REWARD " + (i + 1),
        image: "",
        isExpand: false,
        inviteImageUrl: "",
        inviteExpirationTime: null,
      };
    }
    return {
      invites: invites,
      powerBar: {
        audio: "",
        image: "",
        isExpand: false,
      },
    };
  }

  patchOrCreate(emoji): Promise<any> {
    return new Promise((resolve, reject) => {
      this.emojiApi.patchOrCreate(emoji).subscribe((res) => {
        resolve(res);
      }, reject);
    });
  }

  getScreensEmojis() {
    return !this.routeParams.id && this.gameEmojis.length !== 0
      ? this.defaultEmojis.splice(0, 48)
      : this.gameEmojis.filter((item) => item.emoji).length !== 0
      ? _.map(this.gameEmojis, "emoji")
      : this.gameEmojis;
  }

  deleteCookies() {
    localStorage.removeItem("gridType");
    localStorage.removeItem("popUp");
    localStorage.removeItem("gameLogo");
    localStorage.removeItem("logoStretch");
    localStorage.removeItem("gameLogoUrl");
    localStorage.removeItem("menuArt");
    localStorage.removeItem("menuArtUrl");
    localStorage.removeItem("layer");
    localStorage.removeItem("layerUrl");
    localStorage.removeItem("gameMenuArtStretch");
    localStorage.removeItem("selectedPlayers");
    localStorage.removeItem("invites");
    localStorage.removeItem("inviteType");
    localStorage.removeItem("powerBar");
    localStorage.removeItem("prizeMode");
    localStorage.removeItem("prize");
    localStorage.removeItem("prizeForm");
    localStorage.removeItem("emojis");
    localStorage.removeItem("step1");
    localStorage.removeItem("step2");
    localStorage.removeItem("step4");
    localStorage.removeItem("step5");
    localStorage.removeItem("rule");
    localStorage.removeItem("imageRuleStretch");
    localStorage.removeItem("inviteArtStretch");
    localStorage.removeItem("isGameMenuToolbar");
  }

  // upsetEmojis
  upsertEmoji(emojis: any[], action: string, pageIndex: number) {
    return this.emojiApi.upsertEmoji(emojis, action, pageIndex);
  }

  upsertGame(
    game: any,
    emojis: any[],
    prizes: any[],
    rewards: any[],
    action: string
  ) {
    return this.gameApi.upsertGame(game, emojis, prizes, rewards, action);
  }

  /**
   * Select the appropriate emojis based on the edited game id
   * @param gameId
   * @param emojis
   * @returns Emoji[]
   */
  reorganizeEmojisAndConcatenate(gameId, emojis): any[] {
    if (!Array.isArray(emojis)) return [];
    return gameId !== undefined && gameId !== ""
      ? emojis.map((el: any) => {
          if (!el) return el;
          const target = el.emoji ? el.emoji : el;
          const rawImg = (el.emoji && el.emoji.image) ? el.emoji.image : (el.image ? el.image : "");
          return {
            ...target,
            image: rawImg ? rawImg.replace(environment.s3BucketUrl, "") : "",
            isActive: target.isActive !== false
          };
        })
      : emojis.map((el) => {
          if (!el) return el;
          return {
            ...el,
            image: el.image ? el.image.replace(environment.s3BucketUrl, "") : "",
            isActive: el.isActive !== false
          };
        });
  }

  clearPrizeColorConfettiState(count: number) {
    this.prizeConfetti = fillArray(count, {
      type: "color", // true means it's color confetti
      color: {
        color_1: "#7969ec",
        color_2: "#60e0e6",
        color_3: "#69ddec",
        color_4: "#b0e0e6",
        color_5: "#ff85de",
        color_6: "#00e1ff",
        numberInput: 10,
        radius: 10,
        type: "color",
      },
      emoji: {
        emoji_1: "🌈",
        emoji_2: "⚡",
        emoji_3: "💥",
        emoji_4: "✨",
        emoji_5: "💫",
        emoji_6: "🌸",
        numberInput: 10,
        size: 10,
        type: "emoji",
      },
      canvas: {
        particleCount: 200,
        spread: 100,
        type: "canvas",
      },
    });
  }

  onCategorySelectChange = async ($event, gameId?) => {
    const categoryId = $event.value;
    let filter: any = {
      where: {
        categoryId: categoryId,
        drawDate: {
          gte: moment().toJSON(),
        },
      },
      fields: [
        "id",
        "title",
        "categoryId",
        "drawDate",
        "gameMenuArt",
        "isGameMenuToolbar",
        "textOptions",
        "isPrivate",
        "gameMenuLayer",
        "titleOpt",
        "shadowOpt",
        "categoryOpt"
      ],
      include: {
        relation: "category",
        scope: {
          fields: ["id", "name"],
        },
      },
    };

    if (gameId) filter.where.id = { neq: gameId };

    this.similarGames = await this.gameApi.find(filter).toPromise();
  };

  // Put this in a service or component
  getImageUrl(image: string): string {
    if (!image) {
      return "/assets/images/image-not-found.png";
    }

    // Check if it's a base64 string
    if (typeof image === "string" && image.indexOf("data:") === 0) {
      return image;
    }

    // Otherwise, assume it's an S3 file name and prepend bucket URL
    return environment.s3BucketUrl + image;
  }
}
